import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ConditionalReadingPage from './ConditionalReadingPage';
import StanzaCard from '../StanzaCard/StanzaCard';
import SummaryCard from '../SummaryCard/SummaryCard';
import {
  loadAdaptedContent,
  loadOriginalContent,
  loadSummary,
  transformFragmentsToStanzas,
  normalizeContentFragments,
  mergeOriginalText,
  updateStanzaNumbers,
  getPlaceholderSummary
} from '../../utils/contentLoaders';
import { getBookConfig } from '../../config/bookRegistry';
import { calculateFragmentRange } from '../../utils/fragmentRangeCalculator';
import '../../css/LoadingSpinner.css';

/**
 * DynamicReadingPage - Universal data loader for all books
 *
 * This component handles:
 * - Loading adapted content from JSON files
 * - Loading summaries and audio
 * - Calculating fragment ranges for continuous numbering
 * - Transforming data for ConditionalReadingPage
 */
const DynamicReadingPage = ({
  bookId,
  onLogout,
  showCefrLevel = false,
  customTitleResolver,
  customSubtitleResolver
}) => {
  const { level, chunk } = useParams();
  const [stanzas, setStanzas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fragmentRange, setFragmentRange] = useState({ start: 1, end: 1 });

  const bookConfig = getBookConfig(bookId);

  useEffect(() => {
    const loadContent = async () => {
      if (!bookConfig) {
        setError(`Book configuration not found for: ${bookId}`);
        setLoading(false);
        return;
      }

      if (!level || !chunk) {
        setError('Missing level or chunk parameters');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const [contentData, originalContentData] = await Promise.all([
          loadAdaptedContent(bookId, level, chunk),
          loadOriginalContent(bookId, chunk, level)
        ]);

        const adaptedFragments = normalizeContentFragments(contentData, bookConfig.contentKey);
        const originalFragments = normalizeContentFragments(originalContentData);
        const mergedFragments = mergeOriginalText(adaptedFragments, originalFragments);

        if (mergedFragments.length === 0) {
          throw new Error('No content found in this section');
        }

        const stanzaData = transformFragmentsToStanzas(mergedFragments, {
          bookTitle: contentData.bookTitle || bookConfig.displayName
        });

        const range = await calculateFragmentRange(bookId, level, chunk, bookId);
        setFragmentRange(range);

        const numberedStanzas = updateStanzaNumbers(stanzaData, range.start);
        setStanzas(numberedStanzas);

        try {
          const summaryData = await loadSummary(bookId, level, chunk);
          setSummary(summaryData || getPlaceholderSummary());
        } catch (summaryErr) {
          setSummary(getPlaceholderSummary());
        }
      } catch (err) {
        setError(`Failed to load content: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [bookId, level, chunk, bookConfig]);

  if (loading) {
    return (
      <div className="loading-spinner-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        color: '#d32f2f',
        fontSize: '16px'
      }}>
        <h2>Error Loading Content</h2>
        <p>{error}</p>
        <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
          Book: {bookId} | Level: {level} | Section: {chunk}
        </p>
      </div>
    );
  }

  if (!bookConfig) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#d32f2f' }}>
        <h2>Configuration Error</h2>
        <p>Book "{bookId}" is not configured in the book registry.</p>
      </div>
    );
  }

  const normalizeTitle = (rawTitle) => {
    if (typeof rawTitle !== 'string') return '';
    const trimmed = rawTitle.trim();
    return trimmed.replace(/^Chapter\s+\d+\s*:\s*/i, '').trim();
  };

  const getFallbackTitle = () => {
    const chunkNum = parseInt(chunk.replace('chunk', ''), 10);
    return Number.isNaN(chunkNum) ? 'Chapter' : `Chapter ${chunkNum + 1}`;
  };

  const rawTitle = customTitleResolver
    ? customTitleResolver(chunk)
    : bookConfig.getTitleForChunk(chunk);

  const normalizedTitle = normalizeTitle(rawTitle);
  const title = normalizedTitle || getFallbackTitle();

  const subtitle = customSubtitleResolver
    ? customSubtitleResolver(fragmentRange, level, showCefrLevel, chunk)
    : bookConfig.getSubtitle(fragmentRange, level, showCefrLevel, chunk);

  return (
    <ConditionalReadingPage
      bookId={bookConfig.bookId || bookId}
      bookTitle={bookConfig.displayName}
      chapterTitle={stanzas[0]?.bookTitle || bookConfig.displayName}
      bookRoute={bookConfig.route}
      level={level}
      chunk={chunk}
      stanzas={stanzas}
      summary={summary}
      cssClass="universal-reading-page"
      StanzaComponent={StanzaCard}
      SummaryComponent={SummaryCard}
      onLogout={onLogout}
      customTitle={title}
      customSubtitle={subtitle}
      bookType={bookConfig.type}
    />
  );
};

export default DynamicReadingPage;
